import { Box } from '@mui/material';
import { KeyRound } from 'lucide-react';
import { useRef, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-admin';

import { useHttpClient } from '@/app/providers';
import { Field } from '@/ui/kit';

import { AuthLayout } from './AuthLayout';
import { AuthLink } from './AuthLink';
import { AuthNotice } from './AuthNotice';
import { AuthSubmitButton } from './AuthSubmitButton';
import { setLoginNotice } from './loginNotice';
import { PasswordField } from './PasswordField';
import { FORGOT_PASSWORD_PATH, LOGIN_PATH } from './routes';

/**
 * Second ecran de la recuperation : « Nouveau mot de passe ».
 *
 * Consomme le code recu par e-mail (`POST /auth/reset-password`). Le serveur ne
 * ouvre **aucune session** en retour : on revient a la connexion, avec une
 * confirmation. Il **revoque aussi toutes les sessions du compte**, mobile
 * compris — l'ecran l'annonce avant l'envoi, pas apres.
 *
 * Le code n'est ni conserve, ni place dans l'URL, ni journalise : il vit dans cet
 * etat local et disparait avec l'ecran. Un code perime, deja utilise ou inconnu
 * (410, `invalid_reset_token`) recoit le meme message, comme cote serveur.
 */

/**
 * `IdentityService::MIN_PASSWORD_LENGTH`, que le serveur compare a `strlen`, donc
 * en octets UTF-8 : compter de meme ici ne refuse jamais ce que le serveur accepte.
 */
export const PASSWORD_MIN_LENGTH = 12;

const utf8Length = (value: string): number => new TextEncoder().encode(value).length;

export const ResetPasswordPage = (): ReactElement => {
  const http = useHttpClient();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (inFlight.current || code.trim() === '' || password === '') {
      return;
    }

    setError(null);

    if (utf8Length(password) < PASSWORD_MIN_LENGTH) {
      setPasswordError(`Le mot de passe doit faire au moins ${String(PASSWORD_MIN_LENGTH)} caractères.`);

      return;
    }

    setPasswordError(undefined);
    inFlight.current = true;
    setSubmitting(true);

    http
      .request({
        method: 'POST',
        path: '/auth/reset-password',
        body: { token: code.trim(), password },
        anonymous: true,
      })
      .then(() => {
        setLoginNotice('password_replaced');
        navigate(LOGIN_PATH);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Le mot de passe n'a pas été remplacé. Réessayez.");
      })
      .finally(() => {
        inFlight.current = false;
        setSubmitting(false);
      });
  };

  return (
    <AuthLayout
      title="Nouveau mot de passe"
      documentTitle="Nouveau mot de passe"
      subtitle="Saisissez le code reçu et choisissez votre nouveau mot de passe."
    >
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Field
          label="Code reçu par e-mail"
          size="L"
          icon={KeyRound}
          value={code}
          onChange={setCode}
          autoComplete="one-time-code"
          autoFocus
          required
        />

        <PasswordField
          label="Nouveau mot de passe"
          value={password}
          onChange={(next) => {
            setPassword(next);
            setPasswordError(undefined);
          }}
          autoComplete="new-password"
          hint={`${String(PASSWORD_MIN_LENGTH)} caractères minimum.`}
          error={passwordError}
          required
        />

        <Box sx={{ fontSize: 13, lineHeight: '18px', color: 'tokens.textMuted' }}>
          Une fois le mot de passe remplacé, tous les appareils connectés à ce compte, mobile compris, seront déconnectés.
        </Box>

        <AuthSubmitButton busy={submitting} disabled={code.trim() === '' || password === ''}>
          {submitting ? 'Envoi…' : 'Remplacer le mot de passe'}
        </AuthSubmitButton>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1.5 }}>
        <AuthLink to={LOGIN_PATH}>Retour à la connexion</AuthLink>
        <AuthLink to={FORGOT_PASSWORD_PATH}>Renvoyer un code</AuthLink>
      </Box>
    </AuthLayout>
  );
};
